const { getElement } = window.BAO.Utils

export class DrawerGiftWithPurchase extends customElements.get('site-drawer') {
  _loading = false

  constructor () {
    super()
    // Used for section rendering API
    this.sectionId = 'gift-with-purchase'
  }

  setupListeners () {
    super.setupListeners()

    this.body = getElement('[data-drawer-gift-with-purchase-el="body"]', { context: this })
    this.closeButton = getElement('[data-drawer-gift-with-purchase-el="close"]', { context: this })
  }

  // Opened from the bag, the X goes back to the bag. Opened from anywhere
  // else (the product page's info icon), the X just closes the drawer.
  setCloseTarget (returnToCart) {
    if (!this.closeButton) return

    if (returnToCart) {
      this.closeButton.setAttribute('data-drawers-trigger', 'cart')
      this.closeButton.removeAttribute('data-drawers-close')
    } else {
      this.closeButton.setAttribute('data-drawers-close', '')
      this.closeButton.removeAttribute('data-drawers-trigger')
    }
  }

  // Same loading-class convention as push-cart.js: a boolean property that
  // toggles a CSS class named by the loading-class attribute, showing a
  // spinner overlay while true. Opening the drawer before content exists
  // (see openForTier/openGalleryView below) means a real, possibly slow
  // section fetch is always in flight the moment it appears - without this,
  // that fetch has zero visual feedback and reads as an unresponsive click.
  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val
    this.classList.toggle(this.loadingClass, val)
  }

  get loadingClass () {
    return this.getAttribute('data-loading-class')
  }

  // Entry point - called by GiftWithPurchaseElement.handleTriggerClick
  // (assets/gift-with-purchase.js) for a single-product tier that needs a
  // shade choice, and by GiftWithPurchaseCarousel (assets/gift-with-purchase-
  // carousel.js) for a "Select Shade" tile in a multi-product tier's
  // carousel. Always renders one specific product's shade picker - product
  // choice for a multi-product tier happens in the carousel, not here.
  // Opens immediately with a loading state rather than waiting for the
  // fetch to resolve - see the `loading` getter/setter above.
  openForTier (detail) {
    this.tierDetail = detail
    this.loading = true
    this.openDrawer()
    this.renderDrawerContent(detail.productUrl)
  }

  // Clicking the Free Gift icon swaps this drawer to an image gallery and
  // truncated product info view instead of the shade picker, fetched from
  // its own section (sections/gift-with-purchase-free-gift.liquid).
  async openGalleryView ({ productUrl, rewardTitle, rewardText } = {}) {
    this.loading = true
    this.openDrawer()

    const doc = await this.fetchDoc('gift-with-purchase-free-gift', productUrl)
    if (!doc) {
      this.loading = false
      return
    }

    const title = doc.querySelector('[data-gift-with-purchase-gallery-el="title"]')
    if (title) {
      title.dataset.defaultTitle ??= title.textContent
      title.textContent = rewardTitle || title.dataset.defaultTitle
    }

    const text = doc.querySelector('[data-gift-with-purchase-gallery-el="reward-text"]')
    if (text) {
      text.textContent = rewardText || ''
      text.setAttribute('aria-hidden', rewardText ? 'false' : 'true')
    }

    this.updateBodyContent(doc.body.innerHTML, null, 'gallery')
  }

  async renderDrawerContent (productUrl) {
    const doc = await this.fetchDoc(this.sectionId, productUrl)
    if (!doc) {
      this.loading = false
      return
    }

    // Try multiple selectors to find the form
    let target = doc.querySelector('#product-form-gift-with-purchase')

    if (!target) {
      target = doc.querySelector('form[data-product-form]')
    }

    if (!target) {
      target = doc.querySelector('form[data-quickbuy-form]')
    }

    if (target && this.tierDetail) {
      // Number.isInteger, not ?? - a NaN tierIndex would serialise as the
      // string "NaN" and never match a real tier index again.
      const properties = {
        _gwp: 'true',
        _gwp_campaign: this.tierDetail.campaignId || '',
        _gwp_tier: Number.isInteger(this.tierDetail.tierIndex)
          ? String(this.tierDetail.tierIndex)
          : '',
        _hiddenFreeGift: 'true',
        _removeEditableVariantButton: 'true',
      }

      for (const [name, value] of Object.entries(properties)) {
        const input = doc.createElement('input')
        input.type = 'hidden'
        input.name = `properties[${name}]`
        input.value = value
        target.appendChild(input)
      }

      // The fetched quick-buy form defaults to quantity 1, so a tier
      // configured to give more than one gift would silently add a single
      // unit through the drawer while the auto-add path gave the full amount.
      const tierQuantity = parseInt(this.tierDetail.quantity, 10)

      if (Number.isInteger(tierQuantity) && tierQuantity > 1) {
        const quantityInput = target.querySelector('input[name="quantity"]')

        if (quantityInput) {
          quantityInput.value = String(tierQuantity)
        } else {
          const input = doc.createElement('input')
          input.type = 'hidden'
          input.name = 'quantity'
          input.value = String(tierQuantity)
          target.appendChild(input)
        }
      }

      // Same clear as handlePromptClick's own add path (gift-with-purchase.js)
      // - the customer's own explicit choice always adds, regardless of any
      // earlier manual removal for this tier.
      if (this.tierDetail.campaignId && Number.isInteger(this.tierDetail.tierIndex)) {
        window.BAO.GiftWithPurchase.clearManualRemovalFlag(this.tierDetail.campaignId, this.tierDetail.tierIndex)
      }
    }

    this.removeUnofferedVariants(doc, this.tierDetail?.variantIds)

    const lastVariantId = this.tierDetail?.productId
      ? localStorage.getItem(`lastGWPVariant${this.tierDetail.productId}`)
      : null

    const variantId = this.pickVariantId(doc, lastVariantId)

    this.updateBodyContent(doc.body.innerHTML, variantId, 'quick-buy')
  }

  // Sold-out shades are disabled in the fetched form (stock at or below
  // stock.minimum_stock_value counts as sold out, as on the product page).
  // Keeps the customer's last shade if it is still offered and in stock,
  // then the form's own checked shade, then the first one in stock.
  pickVariantId (doc, lastVariantId) {
    const choices = Array.from(doc.querySelectorAll('[data-quick-buy-variant-radios-el="option-choice"]'))
      .filter(input => !input.disabled)

    const lastChoice = choices.find(input => input.dataset.variantId === lastVariantId)
    const checkedChoice = choices.find(input => input.hasAttribute('checked'))

    return (lastChoice ?? checkedChoice ?? choices[0])?.dataset.variantId ?? null
  }

  // A tier can give away only some of a gift's shades (gift_variants in the
  // app). The discount Function only frees those shades, so every other
  // shade is removed from the fetched form before it is shown. variantIds
  // is the comma-wrapped list from gift-with-purchase-allowed-variants.liquid;
  // empty means every shade is offered. A multipack's shades belong to the
  // items inside it, not to the gift variant, so they are never removed.
  removeUnofferedVariants (doc, variantIds) {
    const allowedVariantIds = new Set((variantIds || '').split(',').filter(Boolean))
    if (allowedVariantIds.size === 0 || doc.querySelector('[data-product-multipack]')) return

    doc.querySelectorAll('[data-quick-buy-variant-radios-el="option-choice"]').forEach(input => {
      if (!allowedVariantIds.has(input.dataset.variantId)) {
        input.closest('.prd-GiftWithPurchasePrdForm_Option')?.remove()
      }
    })

    const variantsData = doc.querySelector('[data-quick-buy-variant-radios-el="data"]')
    if (variantsData) {
      const variants = JSON.parse(variantsData.textContent)
      variantsData.textContent = JSON.stringify(variants.filter(variant => allowedVariantIds.has(String(variant.id))))
    }
  }

  // Memoized per page load - the shade picker's markup for a given product
  // doesn't change between opens, so re-opening the same tier/gift product
  // (a common path: open, close, reopen to compare shades) shouldn't pay
  // for another section render. Caches the parsed innerHtml string, not the
  // Document, since a Document can only be queried once its nodes are
  // consumed/cloned elsewhere - callers do their own fresh parse.
  async fetchDoc (sectionId, productUrl) {
    const cacheKey = `${sectionId}|${productUrl}`
    this.docCache ??= new Map()

    if (!this.docCache.has(cacheKey)) {
      this.docCache.set(cacheKey, this.fetchDocUncached(sectionId, productUrl))
    }

    const innerHtml = await this.docCache.get(cacheKey)
    if (!innerHtml) {
      this.docCache.delete(cacheKey)
      return null
    }

    return new DOMParser().parseFromString(innerHtml, 'text/html')
  }

  async fetchDocUncached (sectionId, productUrl) {
    const html = await this.sectionRenderer.fetchSingle(sectionId, productUrl)

    return this.sectionRenderer.getInnerHtml(html)
  }

  openDrawer () {
    const drawers = getElement('site-drawers')

    this.setCloseTarget(drawers?.activeDrawerKey === 'cart')
    drawers?.open(this.getAttribute('key'))
  }

  closeDrawer () {
    getElement('site-drawers')?.closeDrawer(this.getAttribute('key'))
  }

  updateBodyContent (content, variantId, view) {
    this.body.innerHTML = ''
    this.body.insertAdjacentHTML('beforeend', content)

    this.classList.remove('drw-GiftWithPurchase-quickBuy', 'drw-GiftWithPurchase-gallery')
    this.classList.add(view === 'gallery' ? 'drw-GiftWithPurchase-gallery' : 'drw-GiftWithPurchase-quickBuy')

    if (variantId) {
      this.querySelector(`input[data-variant-id="${variantId}"]`)?.click()
    }

    // The drawer is already open by this point (openForTier/openGalleryView
    // open it immediately, before this fetch resolves) - this just clears
    // the loading state now that real content has replaced it.
    this.loading = false
  }

  /**
   * @returns {sectionRenderer} The BAO sectionRenderer class
   */
  get sectionRenderer () {
    return window.BAO.sectionRenderer
  }
}

customElements.define('drawer-gift-with-purchase', DrawerGiftWithPurchase)
